import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  TextField,
  MenuItem,
  TableSortLabel,
  CircularProgress,
} from '@mui/material';
import type { RealtimeQuote } from '../types';
import { getPriceColor, formatPct, formatVolume, formatAmount, formatNumber } from '../utils/format';

interface Column {
  key: keyof RealtimeQuote;
  label: string;
  width: number;
  format?: (val: any, row: RealtimeQuote) => React.ReactNode;
  align?: 'left' | 'right' | 'center';
}

interface StockTableProps {
  quotes: RealtimeQuote[];
  total: number;
  loading: boolean;
  sort: string;
  order: 'asc' | 'desc';
  onSortChange: (sort: string, order: 'asc' | 'desc') => void;
  search: string;
  onSearchChange: (search: string) => void;
  onRowAction?: (quote: RealtimeQuote) => void;
  actionLabel?: string;
  rowHeight?: number;
  maxHeight?: number;
}

/**
 * 通用股票表格组件
 * 支持排序、搜索、虚拟滚动（处理5000+行）
 * A股配色：红涨绿跌
 */
export default function StockTable({
  quotes,
  total,
  loading,
  sort,
  order,
  onSortChange,
  search,
  onSearchChange,
  onRowAction,
  actionLabel,
  rowHeight = 40,
  maxHeight = 600,
}: StockTableProps) {
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 50 });
  const containerRef = useRef<HTMLDivElement>(null);

  // 列定义 — 优化宽度防止换行，数字列等宽对齐
  const columns: Column[] = useMemo(
    () => [
      { key: 'code', label: '代码', width: 100 },
      { key: 'name', label: '名称', width: 110 },
      {
        key: 'price',
        label: '现价',
        width: 75,
        align: 'right',
        format: (val, row) => (
          <span style={{ color: getPriceColor(row.changePct), fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
            {formatNumber(val)}
          </span>
        ),
      },
      {
        key: 'changePct',
        label: '涨跌幅',
        width: 85,
        align: 'right',
        format: (val) => (
          <span style={{ color: getPriceColor(val), fontWeight: 600, fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
            {formatPct(val)}
          </span>
        ),
      },
      {
        key: 'changeAmount',
        label: '涨跌额',
        width: 80,
        align: 'right',
        format: (val) => (
          <span style={{ color: getPriceColor(val), fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
            {val > 0 ? '+' : ''}{formatNumber(val)}
          </span>
        ),
      },
      {
        key: 'turnoverRate',
        label: '换手率',
        width: 80,
        align: 'right',
        format: (val) => (
          <span style={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
            {formatNumber(val)}%
          </span>
        ),
      },
      {
        key: 'volume',
        label: '成交量',
        width: 100,
        align: 'right',
        format: (val) => (
          <span style={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
            {formatVolume(val)}
          </span>
        ),
      },
      {
        key: 'amount',
        label: '成交额',
        width: 100,
        align: 'right',
        format: (val) => (
          <span style={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
            {formatAmount(val)}
          </span>
        ),
      },
      {
        key: 'high',
        label: '最高',
        width: 75,
        align: 'right',
        format: (val) => (
          <span style={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
            {formatNumber(val)}
          </span>
        ),
      },
      {
        key: 'low',
        label: '最低',
        width: 75,
        align: 'right',
        format: (val) => (
          <span style={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
            {formatNumber(val)}
          </span>
        ),
      },
    ],
    []
  );

  // 虚拟滚动处理
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollTop = container.scrollTop;
      const start = Math.floor(scrollTop / rowHeight);
      const end = Math.min(start + Math.ceil(maxHeight / rowHeight) + 5, quotes.length);
      setVisibleRange({ start, end });
    };

    container.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => container.removeEventListener('scroll', handleScroll);
  }, [quotes.length, rowHeight, maxHeight]);

  const visibleQuotes = quotes.slice(visibleRange.start, visibleRange.end);

  const handleSort = (columnKey: string) => {
    const isAsc = sort === columnKey && order === 'asc';
    onSortChange(columnKey, isAsc ? 'desc' : 'asc');
  };

  return (
    <Box>
      {/* 搜索框 */}
      <Box sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="搜索代码或名称..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{ width: 250 }}
        />
        <Box sx={{ color: 'text.secondary', fontSize: 13 }}>
          共 {total} 只股票
        </Box>
        {loading && <CircularProgress size={16} />}
      </Box>

      <TableContainer
        component={Paper}
        ref={containerRef}
        sx={{ maxHeight, overflow: 'auto', position: 'relative' }}
      >
        {/* 虚拟滚动占位 */}
        <div style={{ height: quotes.length * rowHeight, position: 'relative' }}>
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    sx={{
                      width: col.width,
                      minWidth: col.width,
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontWeight: 600,
                      bgcolor: 'grey.100',
                      fontSize: 13,
                      py: 1,
                      px: 1.5,
                    }}
                    align={col.align || 'left'}
                  >
                    <TableSortLabel
                      active={sort === col.key}
                      direction={sort === col.key ? order : 'asc'}
                      onClick={() => handleSort(col.key as string)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                {onRowAction && (
                  <TableCell
                    sx={{
                      width: 80,
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      fontWeight: 600,
                      bgcolor: 'grey.100',
                      fontSize: 13,
                      py: 1,
                      px: 1.5,
                    }}
                    align="center"
                  >
                    操作
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleQuotes.map((quote, idx) => {
                const actualIndex = visibleRange.start + idx;
                return (
                  <TableRow
                    key={quote.code}
                    hover
                    className="stock-row"
                    sx={{
                      height: rowHeight,
                      position: 'absolute',
                      top: actualIndex * rowHeight,
                      width: '100%',
                      left: 0,
                    }}
                  >
                    {columns.map((col) => (
                      <TableCell
                        key={col.key}
                        sx={{
                          width: col.width,
                          minWidth: col.width,
                          py: 0.75,
                          px: 1.5,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontSize: 13,
                        }}
                        align={col.align || 'left'}
                      >
                        {col.key === 'code' ? (
                          <span style={{ fontFamily: 'monospace', fontVariantNumeric: 'tabular-nums' }}>
                            {String(quote[col.key] ?? '')}
                          </span>
                        ) : col.key === 'name' ? (
                          <span style={{ fontWeight: 500 }}>
                            {String(quote[col.key] ?? '')}
                          </span>
                        ) : col.format ? (
                          col.format(quote[col.key], quote)
                        ) : (
                          String(quote[col.key] ?? '')
                        )}
                      </TableCell>
                    ))}
                    {onRowAction && (
                      <TableCell sx={{ py: 0.75, px: 1.5 }} align="center">
                        <Box
                          component="button"
                          onClick={() => onRowAction(quote)}
                          sx={{
                            border: '1px solid',
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            bgcolor: 'transparent',
                            borderRadius: 1,
                            px: 1.5,
                            py: 0.25,
                            cursor: 'pointer',
                            fontSize: 12,
                            '&:hover': { bgcolor: 'primary.main', color: '#fff' },
                          }}
                        >
                          {actionLabel || '买入'}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </TableContainer>
    </Box>
  );
}
